import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';

import { InputTooltipOptions, PopoverOptions, PopoverPosition } from '../../../../../../store/models/popover.model';
import { RegisterForm, RegisterFormState } from '../../../../../../store/models/register.model';
import { UserType } from 'app/store/models/user.model';
import { UtilitiesService } from 'app/store/services/utilities.servive';
import { ButtonComponent } from '../../../../../ui/button/button.component';

@Component({
  selector: 'app-register-step-create',
  templateUrl: './register-step-create.component.html',
  styleUrls: ['./register-step-create.component.scss'],
})
export class RegisterStepCreateComponent implements OnInit {
  @Input() isEmailTaken = false;
  @Input() emailErrors: string[] = [];

  @Output() Submit: EventEmitter<RegisterForm> =
    new EventEmitter<RegisterForm>();

  @ViewChild('buttonComponent') buttonComponent: ButtonComponent;

  popoverData: PopoverOptions = {
    canClose: false,
    body: 'Select your role by using the radio buttons below',
    position: PopoverPosition.BOTTOM,
    asTooltip: false,
    visible: true,
  };

  popoverDataInputField: PopoverOptions = {
    title: 'Hint!',
    canClose: true,
    body: 'Fill the information and click Next at the bottom to continue',
    position: PopoverPosition.RIGHT,
    asTooltip: false,
    visible: true,
  };

  inputTooltipOptions: InputTooltipOptions = {
    text: 'Please select your timezone.',
  };

  registerForm: FormGroup;

  timezones = {
    US: {
      'Eastern Time': 'Eastern Time (US & Canada)',
      'Alaska Time': 'Alaska',
      'Hawaii-Aleutian Time': 'Hawaii',
      'Pacific Time': 'Pacific Time (US & Canada)',
      'Mountain Time': 'Mountain Time (US & Canada)',
      'Central Time': 'Central Time (US & Canada)',
      Arizona: 'Mountain Time (US & Canada)',
    },
    CA: {
      'Pacific Time': 'Pacific Time (US & Canada)',
      'Mountain Time': 'Mountain Time (US & Canada)',
      'Central Time': 'Central Time (US & Canada)',
      'Eastern Time': 'Eastern Time (US & Canada)',
      'Atlantic Time': 'Atlantic Time (Canada)',
      'Newfoundland Time': 'Newfoundland',
    },
  };

  selectedCountry = 'US';

  constructor(
    private registerStore: Store<RegisterFormState>,
    private utilService: UtilitiesService,
  ) {
  }

  get state() {
    return this.registerForm.get('state');
  }

  get country() {
    return this.registerForm.get('country');
  }

  get address2() {
    return this.registerForm.get('address2');
  }

  get address1() {
    return this.registerForm.get('address1');
  }

  get confirm_password() {
    return this.registerForm.get('confirm_password');
  }

  get password() {
    return this.registerForm.get('password');
  }

  get city() {
    return this.registerForm.get('city');
  }

  get phone_number() {
    return this.registerForm.get('phone_number');
  }

  get company() {
    return this.registerForm.get('company');
  }

  get last_name() {
    return this.registerForm.get('last_name');
  }

  get first_name() {
    return this.registerForm.get('first_name');
  }

  get user_type() {
    return this.registerForm.get('user_type');
  }

  get user_dst_bit() {
    return this.registerForm.get('user_dst_bit');
  }

  get email() {
    return this.registerForm.get('email');
  }

  get time_zone() {
    return this.registerForm.get('time_zone');
  }

  get zip() {
    return this.registerForm.get('zip');
  }

  getTimezones(country: string) {
    const timezone = this.timezones[country];

    return Object.keys(timezone).map(k => ({ label: k, value: timezone[k] }));
  }

  objectKeys(items) {
    if (!items) {
      return [];
    }

    return Object.keys(items);
  }

  getStates(country: string) {
    const states = this.utilService.getStates()[country];
    return Object.keys(states).map(k => ({ label: states[k], value: k }));
  }

  validatePasswordStrength(c: FormControl) {
    const numberRegex = new RegExp('^(?=.*[0-9])');
    const lowercaseRegex = new RegExp('^(?=.*[a-z])');
    const uppercaseRegex = new RegExp('^(?=.*[A-Z])');
    const specialCharRegex = new RegExp('^(?=.*\\W)');

    const errors: any = {};

    if (!c.value.match(numberRegex)) {
      errors.numberValue = true;
    }

    if (!c.value.match(lowercaseRegex)) {
      errors.lowercaseValue = true;
    }

    if (!c.value.match(uppercaseRegex)) {
      errors.uppercaseValue = true;
    }

    if (!c.value.match(specialCharRegex)) {
      errors.specialCharValue = true;
    }

    return Object.keys(errors).length === 0 ? null : errors;
  }

  ngOnInit() {
    // this.closingPopover.onClose().subscribe(() => this.infoPopOver.hide());
    this.registerForm = new FormGroup({
      user_type: new FormControl(UserType.SINGLE_USER, [Validators.required]),
      email: new FormControl('', [Validators.required, Validators.min(6)]),
      first_name: new FormControl('', [Validators.required]),
      last_name: new FormControl(''),
      company: new FormControl('', [Validators.required]),
      phone_number: new FormControl('', [Validators.pattern('^[- +()0-9]+$')]),
      address1: new FormControl('', [Validators.required]),
      address2: new FormControl(''),
      country: new FormControl('', [Validators.required]),
      city: new FormControl('', [Validators.required]),
      state: new FormControl('', [Validators.required]),
      zip: new FormControl('', [Validators.required]),
      time_zone: new FormControl('', [Validators.required]),
      user_dst_bit: new FormControl(false),
      password: new FormControl('', [
        Validators.required,
        Validators.minLength(8),
        this.validatePasswordStrength,
      ]),
      terms_accepted: new FormControl('', [Validators.required]),
      confirm_password: new FormControl('', [
        Validators.required,
        Validators.minLength(8),
        (control: AbstractControl) => {
          if (!this.registerForm) {
            return null;
          }

          const password = this.registerForm.get('password');

          if (password.value !== control.value) {
            return {
              not_same_as: true,
            };
          }
        },
      ]),
    });
  }

  submitForm = () => {
    if (this.registerForm.valid && this.registerForm.get('terms_accepted').value) {
      if (
        this.user_type.value === UserType.SINGLE_USER &&
        this.company.value.length === 0
      ) {
        this.company.setErrors({
          required: true,
        });

        this.registerForm.markAllAsTouched();

        return false;
      }

      const form = this.registerForm.getRawValue();

      localStorage.setItem('email', form.email);

      this.Submit.emit(this.registerForm.getRawValue());
    } else {
      this.buttonComponent.release();
      this.registerForm.markAllAsTouched();
    }
  };

  onCountrySelect(event) {
    const value = event;

    if (value !== 0) {
      this.selectedCountry = value;
    }
  }

  hasControlErrors(control: AbstractControl) {
    return control.invalid && (control.dirty || control.touched);
  }

  selectType(user: string) {
    this.user_type.setValue(user);
  }

  getCountryItems() {
    return [
      {
        label: 'United States',
        value: 'US',
      },
      {
        label: 'Canada',
        value: 'CA',
      },
    ];
  }
}
